// Navegação mobile: top bar + bottom nav + drawer lateral.
// Aparece só em <md. Em ≥md a Navbar desktop tradicional é mostrada.

import { useState, useEffect } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, ClipboardList, Kanban, Truck, Plus,
  Menu, X, Settings, LogOut, Wrench, BarChart3, Users, DollarSign,
  Sparkles, Wallet,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'

// 4 itens principais na bottom nav + botão central de "Novo Pedido" (FAB)
const BOTTOM_LINKS = [
  { to: '/',          label: 'Início',    icon: LayoutDashboard },
  { to: '/pedidos',   label: 'Pedidos',   icon: ClipboardList   },
  // espaço pro FAB central
  { to: '/kanban',    label: 'Kanban',    icon: Kanban          },
  { to: '/logistica', label: 'Logística', icon: Truck           },
]

// Demais links vão pro drawer
const DRAWER_LINKS = [
  { to: '/dashboards',         label: 'Dashboards',icon: BarChart3  },
  { to: '/clientes',           label: 'Clientes',  icon: Users      },
  { to: '/comissoes',          label: 'Comissões', icon: DollarSign },
  { to: '/contas-a-receber',   label: 'A receber', icon: Wallet     },
  { to: '/oportunidades-arco', label: 'Arco',      icon: Sparkles   },
  { to: '/custo',              label: 'Formini',   icon: Wrench     },
  { to: '/config',             label: 'Config',    icon: Settings   },
]

export function MobileNav() {
  const { profile, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [drawerAberto, setDrawerAberto] = useState(false)

  // Fecha drawer ao trocar de rota
  useEffect(() => {
    setDrawerAberto(false)
  }, [location.pathname])

  // Bloqueia scroll do body quando drawer está aberto
  useEffect(() => {
    if (drawerAberto) document.body.style.overflow = 'hidden'
    else                document.body.style.overflow = ''
    return () => { document.body.style.overflow = '' }
  }, [drawerAberto])

  return (
    <>
      {/* ─────────────────────────────────────────────────────────────────────
          TOP BAR mobile — logo + hamburger
          ─────────────────────────────────────────────────────────────────── */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-[#111111] h-12 flex items-center px-3 gap-3 shadow-lg md:hidden">
        <NavLink to="/" className="flex items-center gap-2 shrink-0">
          <img
            src="/favicon.svg"
            alt="Mini Marcenaria"
            className="h-8 w-8 rounded-lg object-contain"
          />
          <div className="leading-none">
            <div className="text-white font-black text-xs tracking-widest uppercase">MINI</div>
            <div className="text-[#F5C200] font-semibold text-[7px] tracking-[0.2em] uppercase mt-0.5">DECK</div>
          </div>
        </NavLink>

        <div className="flex-1" />

        <button
          onClick={() => setDrawerAberto(true)}
          title="Menu"
          className="p-2 rounded-lg text-slate-300 hover:bg-white/10 active:bg-white/20 transition-colors"
        >
          <Menu size={20} />
        </button>
      </header>

      {/* ─────────────────────────────────────────────────────────────────────
          BOTTOM NAVIGATION — 4 links + FAB central de "Novo Pedido"
          ─────────────────────────────────────────────────────────────────── */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 h-16 flex md:hidden shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
        {/* 2 links da esquerda */}
        {BOTTOM_LINKS.slice(0, 2).map(link => <BottomTab key={link.to} {...link} />)}

        {/* FAB central — Novo Pedido */}
        <div className="relative w-16 flex items-center justify-center">
          <NavLink
            to="/pedido/novo"
            className="absolute -top-5 w-14 h-14 rounded-full bg-[#F5C200] text-[#111111] flex items-center justify-center shadow-lg hover:shadow-xl active:scale-95 transition-all"
            title="Novo Pedido"
          >
            <Plus size={26} strokeWidth={3} />
          </NavLink>
        </div>

        {/* 2 links da direita */}
        {BOTTOM_LINKS.slice(2).map(link => <BottomTab key={link.to} {...link} />)}
      </nav>

      {/* ─────────────────────────────────────────────────────────────────────
          DRAWER lateral — outros links + perfil + logout
          ─────────────────────────────────────────────────────────────────── */}
      {drawerAberto && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/50 md:hidden"
            onClick={() => setDrawerAberto(false)}
          />
          <aside className="fixed top-0 right-0 bottom-0 z-50 w-72 max-w-[85vw] bg-white shadow-2xl md:hidden flex flex-col animate-slide-in">
            <div className="bg-[#111111] px-4 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <img src="/favicon.svg" alt="" className="h-9 w-9 rounded-lg" />
                <div className="leading-none">
                  <div className="text-white font-black text-sm tracking-widest uppercase">MINI DECK</div>
                  {profile?.nome && (
                    <div className="text-[#F5C200] text-[10px] mt-1">{profile.nome}</div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setDrawerAberto(false)}
                className="p-1.5 rounded-lg text-slate-300 hover:bg-white/10"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-2">
              {DRAWER_LINKS.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors
                     ${isActive
                       ? 'bg-[#F5C200]/10 text-[#CC8800] border-r-4 border-[#F5C200]'
                       : 'text-slate-700 hover:bg-slate-50 active:bg-slate-100'}`
                  }
                >
                  <Icon size={18} />
                  {label}
                </NavLink>
              ))}
            </div>

            <button
              onClick={() => { setDrawerAberto(false); logout() }}
              className="flex items-center gap-3 px-4 py-4 text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 border-t border-slate-100 shrink-0"
            >
              <LogOut size={18} />
              Sair
            </button>
          </aside>
        </>
      )}
    </>
  )
}

function BottomTab({ to, label, icon: Icon }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold transition-colors
         ${isActive ? 'text-[#CC8800]' : 'text-slate-500 active:text-slate-800'}`
      }
    >
      <Icon size={20} />
      <span className="leading-none">{label}</span>
    </NavLink>
  )
}
